// The search box in the top bar: one query over everything, grouped by kind.
//
// WHY A DROPDOWN AND A PAGE. The dropdown answers "take me there" — the common
// case, where you know what you are looking for and want to be gone in two
// keystrokes. The page answers "show me everything about this", which is the
// case notes created: a phrase can match a dozen passages and reading them is
// the point, not navigating away from them.
//
// Groups are never interleaved, here or on the page. The API returns them
// separately for a reason — a deal matched on its customer's name and a note
// matched on a paragraph are not comparable — and flattening them in the UI
// would put back exactly the false ordering the API refused to invent.

import { useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import type { SearchHit } from "@crm/shared";
import styles from "./GlobalSearch.module.css";
import { SearchInput } from "../design/SearchInput.js";
import { useGlobalSearch } from "../api/queries.js";
import { hitLabel, hitTarget } from "./searchHits.js";

/**
 * How many of each kind the dropdown shows. Small on purpose: it is a jump-to,
 * and a list long enough to scroll is a list you should have opened the page
 * for. The group header carries the true total, so five of twelve still says
 * twelve.
 */
const DROPDOWN_LIMIT = 4;

export function GlobalSearch(): ReactNode {
  const navigate = useNavigate();
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const container = useRef<HTMLDivElement>(null);
  const inputWrap = useRef<HTMLDivElement>(null);

  // SearchInput already debounces and commits on a pause, so `query` changes
  // at most every 300ms however fast somebody types — which is what keeps the
  // note half from embedding a query per keystroke.
  const results = useGlobalSearch(query, DROPDOWN_LIMIT);

  // One flat list for the keyboard only. The RENDER stays grouped; arrowing
  // through results has to cross group boundaries or the keyboard cannot reach
  // the second group at all.
  const flat = useMemo<readonly SearchHit[]>(
    () => (results.data?.groups ?? []).flatMap((group) => group.hits),
    [results.data],
  );

  useEffect(() => {
    setActive(0);
  }, [flat]);

  // A click anywhere else closes it. Pointerdown rather than click so the
  // dropdown is gone before whatever was clicked reacts.
  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent): void {
      if (!container.current?.contains(event.target as Node)) setOpen(false);
    }
    document.addEventListener("pointerdown", onPointerDown);
    return () => document.removeEventListener("pointerdown", onPointerDown);
  }, [open]);

  // ⌘K / Ctrl-K focuses the box from anywhere, which is where every hand
  // already reaches.
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent): void {
      if (event.key.toLowerCase() !== "k" || !(event.metaKey || event.ctrlKey)) return;
      event.preventDefault();
      inputWrap.current?.querySelector("input")?.focus();
      setOpen(true);
    }
    document.addEventListener("keydown", onKeyDown);
    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  function go(hit: SearchHit): void {
    setOpen(false);
    const target = hitTarget(hit);
    if (target === null) return;
    void navigate(target);
  }

  function onKeyDown(event: React.KeyboardEvent): void {
    switch (event.key) {
      case "ArrowDown":
        event.preventDefault();
        setActive((i) => (flat.length === 0 ? 0 : (i + 1) % flat.length));
        return;
      case "ArrowUp":
        event.preventDefault();
        setActive((i) => (flat.length === 0 ? 0 : (i - 1 + flat.length) % flat.length));
        return;
      case "Enter": {
        event.preventDefault();
        const hit = flat[active];
        // Enter with nothing highlighted means "show me everything", which is
        // the page — not a silent no-op.
        if (hit === undefined) {
          if (query.trim() !== "") {
            setOpen(false);
            void navigate({ to: "/search", search: { q: query.trim() } });
          }
          return;
        }
        go(hit);
        return;
      }
      case "Escape":
        setOpen(false);
        return;
      default:
    }
  }

  const groups = results.data?.groups ?? [];
  const asked = query.trim() !== "";

  return (
    <div className={styles.root} ref={container} onKeyDown={onKeyDown}>
      <div ref={inputWrap}>
        <SearchInput
          value={query}
          onCommit={(next) => {
            setQuery(next);
            setOpen(true);
          }}
          placeholder="Search deals, partners, notes…  ⌘K"
          ariaLabel="Search the CRM"
          className={styles.input}
        />
      </div>

      {open && asked && (
        <div className={styles.dropdown} role="listbox" aria-label="Search results">
          {results.isPending && <div className={styles.note}>Searching…</div>}

          {!results.isPending && groups.length === 0 && (
            <div className={styles.note}>
              Nothing matched “{query.trim()}”.
              {/* The honest footnote: an empty answer over an unembedded
                  corpus is not the same as an empty corpus, and the API says
                  which this is. */}
              {(results.data?.pending_notes ?? 0) > 0 && (
                <span className={styles.pending}>
                  {" "}
                  {results.data?.pending_notes} note
                  {results.data?.pending_notes === 1 ? " is" : "s are"} still being indexed and can
                  only be found by word so far.
                </span>
              )}
            </div>
          )}

          {groups.map((group) => (
            <div key={group.kind} className={styles.group}>
              <div className={styles.groupHeader}>
                <span>{hitLabel(group.kind)}</span>
                <span className={styles.groupTotal}>{group.total}</span>
              </div>
              {group.hits.map((hit) => {
                const index = flat.indexOf(hit);
                return (
                  <button
                    key={`${hit.kind}:${hit.id}`}
                    type="button"
                    role="option"
                    aria-selected={index === active}
                    className={`${styles.hit} ${index === active ? styles.hitActive : ""}`}
                    onMouseEnter={() => setActive(index)}
                    onClick={() => go(hit)}
                  >
                    <span className={styles.hitTitle}>{hit.title}</span>
                    {hit.subtitle !== null && (
                      <span className={styles.hitSubtitle}>{hit.subtitle}</span>
                    )}
                    {/* The note's own words, not a summary — so the reader can
                        judge the match rather than trust it. */}
                    {hit.snippet !== null && (
                      <span className={styles.hitSnippet}>{hit.snippet}</span>
                    )}
                  </button>
                );
              })}
            </div>
          ))}

          {groups.length > 0 && (
            <button
              type="button"
              className={styles.seeAll}
              onClick={() => {
                setOpen(false);
                void navigate({ to: "/search", search: { q: query.trim() } });
              }}
            >
              See everything for “{query.trim()}”
            </button>
          )}
        </div>
      )}
    </div>
  );
}
